import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridItemProps } from "@insite/mobius/GridItem";
import Img, { ImgProps } from "@insite/mobius/Img";

import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse, { HTMLReactParserOptions } from "html-react-parser";
import React from "react";
import { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface MemberList {
    fields: {
        searchProductKeyword: string;
        title: string;
        heading: string;
        subTitle: string;
        PdfLink: string;
        searchProduct: string;
        downLoadPDF: string;
        tagFields: [];
        pageLinkLabel: string;
        pageLink: LinkFieldValue;
        linkType: "downloadPDF" | "redirectPage";
    };
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        currentLocation: getLocation(state),
    };
};

const enum fields {
    heading = "heading",
    title = "title",
    subTitle = "subTitle",
    downLoadPDF = "downLoadPDF",
    searchProductKeyword = "searchProductKeyword",
    divisions = "divisions",
    PdfLink = "PdfLink",
    tagFields = "tagFields",
    linkType = "linkType",
    pageLinkLabel = "pageLinkLabel",
    backgroundImage = "backgroundImage",
}

interface OwnProps extends WidgetProps {
    fields: {
        members: any;
        [fields.divisions]: MemberList[];
        [fields.title]: string;
        [fields.subTitle]: string;
        [fields.heading]: string;
        [fields.PdfLink]: string;
        [fields.downLoadPDF]: string;
        [fields.tagFields]: [];
        [fields.backgroundImage]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface SearchProductItemStyle {
    mainContainer?: GridItemProps;
    headingWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    subtitleWrapper?: InjectableCss;
    downloadPdfWrapper?: InjectableCss;
    pageLinkStyles?: InjectableCss;
    backgroundImageStyles?: ImgProps;
    overlayStyles: InjectableCss;
}

export const searchProductItemStyle: SearchProductItemStyle = {
    mainContainer: {
        width: [12, 12, 12, 6, 6],
        css: css`
            width: 100%;
            position: relative;
            height: 100%;
            margin-left: 8px;
            background: rgba(38, 107, 64, 0.8);
            padding: 32px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 24px;
                        width: 97%;
                        margin-left: 0px;
                    `,
                    css`
                        margin-top: 24px;
                        width: 97%;
                        margin-left: 0px;
                    `,
                    css`
                        margin-top: 24px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    backgroundImageStyles: {
        css: css`
            position: absolute;
            top: 0;
            left: 0;
            object-fit: cover;
            width: 100%;
            display: block;
            height: 100%;
        `,
    },
    overlayStyles: {
        css: css`
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            mix-blend-mode: overlay;
            background-color: rgba(38, 107, 64, 0.8);
            opacity: 0.8;
        `,
    },
    headingWrapper: {
        css: css`
            color: #ffffff !important;
            font-size: 12px;
            line-height: 16px;
            text-transform: uppercase;
        `,
    },

    titleWrapper: {
        css: css`
            color: #ffffff;
            font-weight: 600;
            line-height: 32px;
            font-size: 24px;
            margin-top: 4px;
        `,
    },
    subtitleWrapper: {
        css: css`
            color: #ffffff;
            font-weight: 400;
            font-size: 14px;
            line-height: 20px;
            margin-top: 24px;
            p {
                font-size: 14px;
            }
        `,
    },

    downloadPdfWrapper: {
        css: css`
            font-size: 14px;
            font-weight: 600;
            line-height: 16px;
            margin-top: 24px;
            align-items: center;
            padding: 8px 16px;
            background: #ffffff;
            border-radius: 16px;
            position: relative;
            z-index: 2;
            opacity: 1;
            span[class^="TypographyStyle-sc"] {
                color: ${getColor("secondary")};
            }
        `,
    },
    pageLinkStyles: {
        css: css`
            font-size: 14px;
            font-weight: 600;
            line-height: 16px;
            margin-top: 24px;
            align-items: center;
            cursor: pointer;
            padding: 8px 16px;
            background: #ffffff;
            color: ${getColor("secondary")};
            display: inline-block;
            border-radius: 16px;
            position: relative;
            z-index: 2;
            opacity: 1;
        `,
    },
};

const styles = searchProductItemStyle;

export const CENSearchAdvertisement: FC<Props> = ({ fields, currentLocation }) => {
    const parsedQuery: any = parseQueryString(currentLocation.search);

    // eslint-disable-next-line array-callback-return
    const listItem = fields.divisions.filter(item => {
        const index = item.fields.tagFields
            ?.map((str: string) => str.replace(/_/g, " "))
            .findIndex((matchedKeyword: string) => matchedKeyword?.toLowerCase() === parsedQuery.query?.toLowerCase());
        if (index !== -1) {
            return item;
        }
    });

    if (listItem.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.mainContainer}>
            <Img src={fields.backgroundImage} altText={fields.heading} {...styles.backgroundImageStyles} />
            <StyledWrapper {...styles.overlayStyles}></StyledWrapper>
            {listItem.map(item => {
                const { url, title } = useGetLink(item.fields.pageLink);
                const history = useHistory();
                const { isInShell } = useShellContext();
                const onClick = () => {
                    if (!url) {
                        return;
                    }

                    if (url.startsWith("http")) {
                        if (isInShell) {
                            return;
                        }

                        window.location.href = url;
                    } else {
                        history.push(url);
                    }
                };

                return (
                    <>
                        <StyledWrapper>
                            <Typography as="p" {...styles.headingWrapper}>
                                {item.fields.heading}
                            </Typography>

                            <Typography as="p" {...styles.titleWrapper}>
                                {item.fields.title}
                            </Typography>

                            <Typography as="p" {...styles.subtitleWrapper}>
                                {parse(transformContentHtml(item.fields.subTitle), parserOptions)}
                            </Typography>
                            {item.fields.linkType === "downloadPDF" && item.fields.downLoadPDF.length > 0 ? (
                                <Link {...styles.downloadPdfWrapper} href={item.fields.PdfLink} target="_blank">
                                    {item.fields.downLoadPDF}
                                </Link>
                            ) : (
                                item.fields.linkType === "redirectPage" &&
                                item.fields.pageLinkLabel.length > 0 && (
                                    <Typography as="p" onClick={onClick} {...styles.pageLinkStyles}>
                                        {item.fields.pageLinkLabel || title || url}
                                    </Typography>
                                )
                            )}
                        </StyledWrapper>
                    </>
                );
            })}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CENSearchAdvertisement),
    definition: {
        group: "Basic",
        displayName: "CEN Search Advertisment",
        fieldDefinitions: [
            {
                name: fields.backgroundImage,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Background Image",
            },
            {
                name: fields.divisions,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.heading;
                },
                defaultValue: [],
                fieldType: "General",
                displayName: "Search Product Item",
                fieldDefinitions: [
                    {
                        name: fields.tagFields,
                        editorTemplate: "TagsField",
                        defaultValue: [],
                        displayName: "Search Keywords",
                    },

                    {
                        name: fields.heading,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.title,
                        editorTemplate: "MultilineTextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.subTitle,
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                    },

                    {
                        name: fields.linkType,
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "DownloadPDF",
                        options: [
                            {
                                displayName: "Download PDF",
                                value: "downloadPDF",
                            },

                            {
                                displayName: "Redirect to Page",
                                value: "redirectPage",
                            },
                        ],
                    },

                    {
                        name: fields.downLoadPDF,
                        editorTemplate: "TextField",
                        displayName: "PDF Label",
                        defaultValue: "",
                        isVisible: item => item.fields.linkType === "downloadPDF",
                    },
                    {
                        name: fields.PdfLink,
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: item => item.fields.linkType === "downloadPDF",
                    },
                    {
                        name: fields.pageLinkLabel,
                        editorTemplate: "TextField",
                        displayName: "Link Label",
                        defaultValue: "",
                        isVisible: item => item.fields.linkType === "redirectPage",
                    },
                    {
                        name: "pageLink",
                        displayName: "Redirection Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isVisible: item => item.fields.linkType === "redirectPage",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
